import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { IGuestToken } from '@core/interfaces/auth/guest-token.interface';
import { AUTH_TOKEN } from '@core/mocks/auth/auth-token.mock';
import { AuthToken } from '@core/models/auth/auth-token.model';
import { environment } from '@env';
import { AAGUID, AppError, Browser, AuthService as CoreAuthService, EncodedHttpParams, IAuthToken, ITranslateConfig, LanguageCode, SsrService, TranslateService, UtilUrl } from '@interticket/core';
import { Observable, Subject, combineLatest, of, throwError } from 'rxjs';
import { catchError, switchMap, switchMapTo, tap } from 'rxjs/operators';
import { CartService } from '../cart/cart.service';
import { IamService } from '../iam/iam.service';
import { MediaService } from '../media/media.service';
import { PartnerService } from '../partner/partner.service';
import { StoreService } from '../store/store.service';

@Injectable()
export class AuthService {

  readonly accountSiteBaseUrl = `${environment.auth.baseUrl || this.document.location.origin}/authentication`;

  private readonly logoutURL = `${this.accountSiteBaseUrl}${environment.auth.logoutUrlPath}`;
  private readonly loginURL = `${this.accountSiteBaseUrl}${environment.auth.loginUrlPath}`;
  private readonly loginParams = environment.auth.login;
  private readonly adminLoginParams = environment.auth.adminLogin;

  get isAuthenticated(): boolean {
    return !!this.storeService.authToken;
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private ssrService: SsrService,
    private iamService: IamService,
    private router: Router,
    private injector: Injector,
    private cartService: CartService,
    private partnerService: PartnerService,
    private storeService: StoreService,
    private mediaService: MediaService,
    private coreAuthService: CoreAuthService
  ) {
    this.checkAuth();
  }

  load(): Observable<any> {
    const hasToken = this.isAuthenticated || this.storeService.guestToken?.accessToken;

    return (hasToken ? of(null) : this.iamService.fetchGuestToken())
      .pipe(switchMapTo(
        combineLatest([
          this.fetchFeatures(),
          this.ifAuthenticated(this.iamService.getProfile()),
          this.ifAuthenticated(this.iamService.getAdminPartners()),
          this.cartService.getCart(),
          this.partnerService.getConfig().pipe(
            switchMap(() => this.injector.get(TranslateService).init(this.getTranslateConfig()))
          ),
          this.ifAuthenticated(this.mediaService.getStreamableEvents()),
        ])
      ));
  }

  private fetchFeatures(): Observable<string[]> {
    return this.iamService.fetchFeatures()
      .pipe(
        catchError((error: AppError) => {
          if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Forbidden && this.isAuthenticated) {
            this.storeService.clearAuthToken();
            this.ssrService.location.reload();
          }
          return throwError(error);
        })
      );
  }

  private getTranslateConfig(): ITranslateConfig {
    return {
      errorRedirectRoute: ['error', 'server-error'],
      language: {
        supported: this.storeService.partnerConfig.general.supportedLanguages,
        default: this.storeService.partnerConfig.general.defaultLanguageCode.toLowerCase() as LanguageCode,
      },
      translationTags: environment.translationTags,
    };
  }

  @Browser()
  refresh(): Observable<any> {
    return this.coreAuthService.refreshAuthToken(
      this.storeService.authToken.access_token,
      this.loginURL,
      this.loginParams
    )
      .pipe(
        tap((token: AuthToken) => this.storeService.authToken = token),
        catchError(e => {
          this.storeService.clearAuthToken();
          this.ssrService.location.reload();

          return new Subject();
        })
      );
  }

  @Browser()
  redirectToAdminSite(partnerId: AAGUID, confirmIdentity: boolean): void {
    this.ssrService.location.href = this.getAdminSiteUrl(partnerId, confirmIdentity);
  }

  getAdminSiteUrl(partnerId: AAGUID, confirmIdentity: boolean): string {
    const httpLoginParams: HttpParams = new EncodedHttpParams({
      fromObject: {
        ...this.adminLoginParams,
        ...confirmIdentity && { identityconfirmed: 'true' },
        partnerId,
      },
    });

    return `${this.loginURL}?${httpLoginParams.toString()}`;
  }

  @Browser()
  redirectToLoginPage(url?: string, partnerIdParam?: string): void {
    const redirectUrl = encodeURIComponent(url || this.ssrService.location.href);

    if (environment.mock || environment.auth.mock) {
      this.router.navigate(['auth'], { queryParams: { ...AUTH_TOKEN, redirectUrl } })
        .then(() => {
          this.ssrService.location.reload();
        });
      return;
    }
    const partnerId = partnerIdParam || this.storeService.partnerConfig.partnerId;
    const redirect_uri = `${this.ssrService.location.origin}/${this.loginParams.redirect_uri}?redirectUrl=${redirectUrl}&`;
    const httpLoginParams: HttpParams = new EncodedHttpParams({
      fromObject: {
        ...this.loginParams,
        partnerId,
        redirect_uri,
        guestId: this.storeService.guestToken.userId,
      },
    });

    this.ssrService.location.href = `${this.loginURL}?${httpLoginParams.toString()}`;
  }

  @Browser()
  redirectToLogoutPage(): void {
    this.storeService.clearAuthToken();

    if (environment.mock || environment.auth.mock) {
      this.ssrService.location.reload();
      return;
    }

    const cartRoute = 'cart';
    const redirectUri = this.ssrService.location.href.includes(`/${cartRoute}`)
      ? this.ssrService.location.origin
      : this.ssrService.location.href;

    const logoutURL = `${this.logoutURL}`
      + `?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`
      + `&id_token_hint=${this.storeService.authToken.access_token}`;

    this.ssrService.location.href = logoutURL;
  }

  ifAuthenticated<T>(observable: Observable<T>): Observable<T> {
    return this.isAuthenticated ? observable : of(null);
  }

  @Browser()
  private checkAuth() {
    // Router snapshot is not available in app initializer
    // Catch auth route and handle authentication
    if (this.ssrService.location.href.includes('/auth?') || UtilUrl.getQueryParam('access_token', this.ssrService.location.href)) {
      this.getAuthToken();
    } else if (UtilUrl.getQueryParam('guest_token', this.ssrService.location.href)) {
      this.getGuestToken();
    }
  }

  @Browser()
  private getAuthToken(): void {
    const href = this.ssrService.location.href;

    const access_token = UtilUrl.getQueryParam('access_token', href);
    const token_type = UtilUrl.getQueryParam('token_type', href);
    const scope = UtilUrl.getQueryParam('scope', href);
    const refresh_token = UtilUrl.getQueryParam('refresh_token', href);
    const expires_in = UtilUrl.getQueryParam('expires_in', href);
    const redirectUrl = UtilUrl.getQueryParam('redirectUrl', href);

    const authToken: IAuthToken = new AuthToken({
      access_token: access_token,
      token_type: token_type,
      scope: scope,
      refresh_token: refresh_token,
      expires_in: +expires_in,
    });

    this.storeService.authToken = authToken;

    // Redirect user before app proceeds to load
    // Overwrite history instead of pushing new item
    // No need for auth route inside of angular app
    this.ssrService.history.replaceState(null, null, redirectUrl);
  }

  @Browser()
  private getGuestToken(): void {
    const accessToken = UtilUrl.getQueryParam('guest_token', this.ssrService.location.href);
    const userId = UtilUrl.getQueryParam('user_id', this.ssrService.location.href);

    const guestToken: IGuestToken = {
      accessToken: `Bearer ${accessToken}`,
      userId,
    };

    this.storeService.guestToken = guestToken;
  }

}
